import React, { useState, useEffect, memo } from "react";
import { Card, Space, DatePicker, Button, Spin, Skeleton, message, Progress, Segmented } from "antd";
import { MetricCardHeader } from "./Shared/MetricCardHeader";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { LikeOutlined, DislikeOutlined, FlagOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getFeedbackAnalysis, getFeedbackAnalysisTrend } from "../../api/api";
import type { FeedbackAnalysisResponse, FeedbackAnalysisTrendResponse } from "../../types/api-models";

const { RangePicker } = DatePicker;

type AggregationType = 'daily' | 'weekly' | 'monthly';

interface FeedbackAnalysisProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const UserFeedbackAnalysis: React.FC<FeedbackAnalysisProps> = ({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [aggregateData, setAggregateData] = useState<FeedbackAnalysisResponse | null>(null);
  const [trendData, setTrendData] = useState<FeedbackAnalysisTrendResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);


  const fetchData = async () => {
    setLoading(true);
    try {
      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setLoading(false);
          return;
        }
        const from_date = dateRange[0].format('YYYY-MM-DD');
        const to_date = dateRange[1].format('YYYY-MM-DD');
        
        const response = await getFeedbackAnalysis(from_date, to_date);
        setAggregateData(response);
        setTrendData(null);
      } else {
        const response = await getFeedbackAnalysisTrend(aggregation as 'weekly' | 'monthly');
        setTrendData(response);
        setAggregateData(null);
      }
    } catch (error) {
      message.error('Failed to load feedback analysis');
      setAggregateData(null);
      setTrendData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip initial fetch in expanded mode
    if (isExpanded) return;
    
    // For collapsed view, use dashboard data
    if (dashboardData?.feedback_analysis) {
      setAggregateData(dashboardData.feedback_analysis);
      setTrendData(null);
    }
  }, [isExpanded, dashboardData]);

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading feedback analysis..." />
      </div>
    );
  }

  // Show empty state with helpful message
  const hasData = (aggregateData && aggregateData.total_messages > 0) || (trendData && trendData.data_points.length > 0);
  
  const emptyStateMessage = !hasData && isExpanded ? (
    <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
      <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to analyze feedback data</p>
      <p style={{ fontSize: 13, color: '#999' }}>Adjust filters above and click "Apply Filters" to view data</p>
    </div>
  ) : !hasData ? (
    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
      No data available
    </div>
  ) : null;

  // If no data, show empty state
  if (!hasData) {
    if (!isExpanded) {
      return emptyStateMessage;
    }
    // In expanded mode, we'll show controls + empty state below
  }

  // Render aggregate view (daily)
  const renderAggregateView = () => {
    if (!aggregateData || aggregateData.total_messages === 0) {
      return emptyStateMessage;
    }

    const feedbackStats = [
      { type: "Thumbs Up", value: aggregateData.thumbs_up, pct: aggregateData.thumbs_up_pct, color: "#52c41a", icon: <LikeOutlined /> },
      { type: "Thumbs Down", value: aggregateData.thumbs_down, pct: aggregateData.thumbs_down_pct, color: "#ff4d4f", icon: <DislikeOutlined /> },
      { type: "Reported", value: aggregateData.reported, pct: aggregateData.reported_pct, color: "#ff7a45", icon: <FlagOutlined /> },
      { type: "No Feedback", value: aggregateData.no_feedback, pct: aggregateData.no_feedback_pct, color: "#d9d9d9", icon: null },
    ];

    return (
      <div className="flex flex-col h-full">
         <div className="mb-4">
            <MetricCardHeader 
              title="User Satisfaction Score"
              value={`${(aggregateData.satisfaction_score || 0).toFixed(1)}%`}
              trend={dashboardData?.feedback_analysis?.satisfaction_trend}
              subtitle="Based on positive vs negative feedback ratio"
            />
         </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        minHeight: isExpanded ? 300 : 250,
        minWidth: 0
      }}>
        <div style={{ flex: "0 0 300px", minWidth: 0, display: "flex", justifyContent: "center" }}>
          <div style={{ width: 300, height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={feedbackStats}
                dataKey="value"
                nameKey="type"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                label={({ name, pct }) => `${pct.toFixed(1)}%`}
                isAnimationActive
              >
                {feedbackStats.map((entry) => (
                  <Cell key={entry.type} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, props: any) =>
                  [`${value} (${props.payload.pct.toFixed(1)}%)`, name]
                }
              />
            </PieChart>
          </ResponsiveContainer>
          </div>
        </div>
        <div style={{
          flex: 1,
          paddingLeft: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}>
          <FeedbackStatsList />
        </div>
      </div>
      </div>
    );
  };

  const FeedbackStatsList = () => {
    if (!aggregateData) return null;
    
    const feedbackStats = [
      { type: "Thumbs Up", value: aggregateData.thumbs_up, pct: aggregateData.thumbs_up_pct, color: "#52c41a", icon: <LikeOutlined /> },
      { type: "Thumbs Down", value: aggregateData.thumbs_down, pct: aggregateData.thumbs_down_pct, color: "#ff4d4f", icon: <DislikeOutlined /> },
      { type: "Reported", value: aggregateData.reported, pct: aggregateData.reported_pct, color: "#ff7a45", icon: <FlagOutlined /> },
      { type: "No Feedback", value: aggregateData.no_feedback, pct: aggregateData.no_feedback_pct, color: "#d9d9d9", icon: null },
    ];
    
    return (
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {feedbackStats.map((item) => (
        <div key={item.type} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16, color: item.color, width: 20 }}>{item.icon}</span>
          <span style={{ color: '#666', fontWeight: 500, flex: 1, fontSize: 13 }}>{item.type}</span>
          <span style={{ color: "#222", fontWeight: 600, fontSize: 14 }}>
            {item.value}{" "}
            <span style={{ color: "#999", fontWeight: 400, fontSize: 12 }}>
              ({item.pct.toFixed(1)}%)
            </span>
          </span>
        </div>
      ))}
      
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>Overall Positive Feedback</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{(aggregateData.overall_positive_pct || 0).toFixed(1)}%</span>
          </div>
          <Progress 
            percent={aggregateData.overall_positive_pct} 
            strokeColor="#52c41a" 
            showInfo={false}
            size="small"
          />
        </div>
        
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>User Satisfaction (excl. No Feedback)</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{(aggregateData.satisfaction_score || 0).toFixed(1)}%</span>
          </div>
          <Progress 
            percent={aggregateData.satisfaction_score} 
            strokeColor="#1890ff" 
            showInfo={false}
            size="small"
          />
        </div>
        
        {isExpanded && aggregateData && (
          <div style={{ marginTop: 12, fontSize: 11, color: '#999', textAlign: 'center' }}>
            Aggregation: {aggregateData.aggregation_used}
          </div>
        )}
      </div>
    </Space>
    );
  };

  // Render trend view (weekly/monthly)
  const renderTrendView = () => {
    if (!trendData || trendData.data_points.length === 0) {
      return emptyStateMessage;
    }

    // Prepare chart data
    const chartData = trendData.data_points.map(point => ({
      period: dayjs(point.period_start).format(aggregation === 'weekly' ? 'MMM DD' : 'MMM YYYY'),
      period_start: point.period_start,
      'Positive': point.thumbs_up_pct,
      'Negative': point.thumbs_down_pct,
      'Reported': point.reported_pct,
      'No Feedback': point.no_feedback_pct,
      total_messages: point.total_messages
    }));

    return (
      <div>
        {/* Trend Chart */}
        <div style={{ minHeight: isExpanded ? 350 : 300, marginBottom: 20 }}>
          <div style={{ height: isExpanded ? 350 : 300 }}>
            <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0]) {
                    return dayjs(payload[0].payload.period_start).format('MMM DD, YYYY');
                  }
                  return label;
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="Positive" stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Negative" stroke="#ff4d4f" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Reported" stroke="#ff7a45" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="No Feedback" stroke="#d9d9d9" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          </div>
        </div>

        {/* Current vs Previous Period */}
        {trendData.current_period && trendData.previous_period && (
          <div style={{ 
            padding: '16px',
            backgroundColor: '#f0f5ff',
            borderRadius: '6px',
            borderLeft: '4px solid #1890ff',
            marginBottom: 16
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#333' }}>
              Period Comparison
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 12 }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Current Period</div>
                <div>Positive: {(trendData.current_period.thumbs_up_pct || 0).toFixed(1)}%</div>
                <div>Negative: {(trendData.current_period.thumbs_down_pct || 0).toFixed(1)}%</div>
                <div>Total Messages: {trendData.current_period.total_messages}</div>
              </div>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Previous Period</div>
                <div>Positive: {(trendData.previous_period.thumbs_up_pct || 0).toFixed(1)}%</div>
                <div>Negative: {(trendData.previous_period.thumbs_down_pct || 0).toFixed(1)}%</div>
                <div>Total Messages: {trendData.previous_period.total_messages}</div>
              </div>
            </div>
          </div>
        )}

        {/* Key Insights */}
        {trendData.insights && trendData.insights.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#333' }}>
              Key Insights
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {trendData.insights.map((insight, index) => (
                <div 
                  key={index}
                  style={{ 
                    padding: '10px 12px',
                    backgroundColor: '#fffbe6',
                    borderRadius: '6px',
                    border: '1px solid #ffe58f',
                    fontSize: 12,
                    color: '#333'
                  }}
                >
                  {insight}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const chartComponent = hasData ? (
    aggregation === 'daily' ? renderAggregateView() : renderTrendView()
  ) : emptyStateMessage;

  // If not expanded, return chart only
  if (!isExpanded) {
    return chartComponent;
  }

  // Expanded view with controls
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
          <Segmented
            value={aggregation}
            onChange={(value) => setAggregation(value as AggregationType)}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' }
            ]}
          />
          {aggregation === 'daily' ? (
            <>
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: 260 }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isAfter(dayjs().endOf('day'));
                }}
              />
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
              Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
            </span>
          )}
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Chart */}
      {chartComponent}
      
      {/* Info footer */}
      {hasData && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          {aggregateData && aggregateData.total_messages > 0 && (
            <>
              Showing {aggregateData.total_messages.toLocaleString()} total messages • 
              Aggregation: {aggregateData.aggregation_used} • 
              Date range: {dayjs(aggregateData.date_range.from).format('MMM D, YYYY')} - {dayjs(aggregateData.date_range.to).format('MMM D, YYYY')}
            </>
          )}
          {trendData && trendData.data_points.length > 0 && (
            <>
              Showing {trendData.data_points.length} {aggregation} periods • 
              Aggregation: {trendData.aggregation} • 
              Date range: {dayjs(trendData.date_range.from).format('MMM D, YYYY')} - {dayjs(trendData.date_range.to).format('MMM D, YYYY')}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MemoizedUserFeedbackAnalysis = memo(UserFeedbackAnalysis);
MemoizedUserFeedbackAnalysis.displayName = 'UserFeedbackAnalysis';

export default MemoizedUserFeedbackAnalysis;
